import { useTranslation } from "react-i18next";
import type { DailySpendDayResponse } from "../../api/reporting";
import { formatDate, formatMoney } from "./formatters";
import { calendarWeeks, spendLevel, todayKeyFor, type SpendLevel } from "./usagePace";
import styles from "./SpendCalendar.module.css";

const weekdayKeys = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"] as const;

const levelClassNames: Record<SpendLevel, string> = {
  0: styles.level0,
  1: styles.level1,
  2: styles.level2,
  3: styles.level3,
  4: styles.level4,
};

/**
 * A calendar heatmap of one reporting period's daily spend (Monday-first weeks, one square per
 * day), shaded by quartile of the period's heaviest day, with the busiest day and the number of days
 * with any spend above it. Each square carries its date and amount as an accessible label, days
 * after today in the current period read as "no data yet", and the colour scale is explained by a
 * Less-to-More legend. The grid is a real table, so screen readers can move across it by day and week.
 *
 * @param props.periodStart - The first day of the period (`YYYY-MM-DD`).
 * @param props.periodEnd - The last day of the period (`YYYY-MM-DD`).
 * @param props.generatedAt - The ISO instant the response was generated, treated as "now".
 * @param props.days - The period's daily spend, one entry per day that has data.
 */
export function SpendCalendar({
  periodStart,
  periodEnd,
  generatedAt,
  days,
}: {
  periodStart: string;
  periodEnd: string;
  generatedAt: string;
  days: DailySpendDayResponse[];
}): React.JSX.Element {
  const { t } = useTranslation();
  const todayKey = todayKeyFor(periodStart, periodEnd, generatedAt);
  const dayByDate = new Map(days.map((day) => [day.date, day]));
  const fallbackCurrency = days[0]?.currency ?? "USD";
  const pastDays = days.filter((day) => day.date <= todayKey);
  const maxAmount = Math.max(0, ...pastDays.map((day) => day.totalAmount));
  const busiest = pastDays.reduce<DailySpendDayResponse | null>((best, day) => (day.totalAmount > (best?.totalAmount ?? 0) ? day : best), null);
  const weeks = calendarWeeks(periodStart, periodEnd);
  const elapsedDays = weeks.flat().filter((dateKey) => dateKey !== null && dateKey <= todayKey).length;
  const activeDays = pastDays.filter((day) => day.totalAmount > 0).length;

  return (
    <div className={styles.panel} data-spotlight>
      <h3 className={`panel-title ${styles.title}`}>{t("consumption.calendar.title")}</h3>
      <dl className={styles.facts}>
        {busiest && (
          <div className={`${styles.fact} ${styles.factFeatured}`}>
            <dt>{t("consumption.calendar.busiestDay")}</dt>
            <dd>
              <span className={styles.factValue}>{formatMoney(busiest.totalAmount, busiest.currency)}</span>{" "}
              <span className={styles.factNote}>{formatShortDay(busiest.date)}</span>
            </dd>
          </div>
        )}
        <div className={styles.fact}>
          <dt>{t("consumption.calendar.activeDays")}</dt>
          <dd>
            <span className={styles.factValue}>{t("consumption.calendar.activeDaysValue", { active: activeDays, total: elapsedDays })}</span>
            {/* Decorative: the figure above says the same. */}
            <span className={styles.factMeter} aria-hidden="true">
              <span style={{ width: `${String(elapsedDays > 0 ? (activeDays / elapsedDays) * 100 : 0)}%` }} />
            </span>
          </dd>
        </div>
      </dl>
      <table className={styles.calendar}>
        <caption className="visually-hidden">{t("consumption.calendar.caption")}</caption>
        <thead>
          <tr>
            {weekdayKeys.map((key) => (
              <th key={key} scope="col" abbr={t(`consumption.calendar.weekdayLong.${key}`)}>
                {t(`consumption.calendar.weekdayShort.${key}`)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {weeks.map((week, weekIndex) => {
            const weekKey = week.find((dateKey) => dateKey !== null) ?? "";
            return (
              <tr key={weekKey}>
                {week.map((dateKey, slot) => {
                  if (dateKey === null) {
                    return <td key={`${weekKey}-${String(slot)}`} className={styles.day} />;
                  }

                  const isPending = dateKey > todayKey;
                  const isToday = dateKey === todayKey;
                  const day = dayByDate.get(dateKey);
                  const amount = isPending ? 0 : (day?.totalAmount ?? 0);
                  const level = isPending ? 0 : spendLevel(amount, maxAmount);
                  const label = isPending
                    ? t("consumption.calendar.dayLabelPending", { date: formatDate(dateKey) })
                    : t("consumption.calendar.dayLabel", {
                        date: formatDate(dateKey),
                        amount: formatMoney(amount, day?.currency ?? fallbackCurrency),
                      });
                  const amountText = isPending ? t("consumption.calendar.dayPending") : formatMoney(amount, day?.currency ?? fallbackCurrency);
                  // The outer columns anchor their tooltip to the cell's own edge so it stays inside the panel.
                  const tooltipSide = slot <= 1 ? styles.tooltipStart : slot >= 5 ? styles.tooltipEnd : "";
                  const isBusiest = !isPending && busiest?.date === dateKey;
                  const swatchClassName = [
                    styles.swatch,
                    levelClassNames[level],
                    isPending ? styles.pending : "",
                    isToday ? styles.today : "",
                    isBusiest ? styles.busiest : "",
                  ]
                    .filter(Boolean)
                    .join(" ");
                  // The cell's place in the grid, so the days can appear one after another.
                  const order = { "--cell": weekIndex * 7 + slot } as React.CSSProperties;

                  return (
                    <td key={dateKey} className={styles.day} aria-current={isToday ? "date" : undefined}>
                      <span className={swatchClassName} style={order} data-level={level} aria-hidden="true">
                        {Number(dateKey.slice(8))}
                      </span>
                      {/* The styled tooltip, not a `title`; it repeats the visually hidden label below, so it is hidden from assistive technology. */}
                      <span className={`chart-tooltip ${styles.tooltip} ${tooltipSide}`} aria-hidden="true">
                        <span className="chart-tooltip-label">{formatDate(dateKey)}</span>
                        <span className={styles.tooltipValue}>{amountText}</span>
                      </span>
                      <span className="visually-hidden">{label}</span>
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
      <div className={styles.legend} aria-hidden="true">
        <span>{t("consumption.calendar.legendLess")}</span>
        {([0, 1, 2, 3, 4] as const).map((level) => (
          <span key={level} className={`${styles.legendSwatch} ${levelClassNames[level]}`} />
        ))}
        <span>{t("consumption.calendar.legendMore")}</span>
      </div>
    </div>
  );
}

/**
 * A short weekday-and-date label for a `YYYY-MM-DD` key, read as UTC, for example `"Thu, Oct 1"`.
 *
 * @param isoDate - The date to format.
 * @returns The short label.
 */
function formatShortDay(isoDate: string): string {
  return new Date(`${isoDate}T00:00:00Z`).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", timeZone: "UTC" });
}
